import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, AlertCircle, User, Target, MapPin, Key, Link as LinkIcon, FileText } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { loadUsers, createAdsRequest, updateAdsRequest } from '../api';

const AdsRequestForm = ({ onClose, onSuccess, request = null, currentUser, tipoSolicitud = 'nueva' }) => {
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [formData, setFormData] = useState({
    tipo_campana: tipoSolicitud,
    plataforma: 'google',
    nombre_cliente: '',
    nombre_campana: '',
    ubicacion_objetivo: '',
    radio_km: '',
    palabras_clave: '',
    presupuesto_diario: '',
    landing_page: '',
    link_cuenta_ads: '',
    asignado_a: '',
    observaciones: '',
    prioridad: 'normal',
    estado: 'pendiente'
  });

  // Cargar usuarios desde la base de datos
  useEffect(() => {
    const loadUsersData = async () => {
      try {
        const users = await loadUsers();
        setAvailableUsers(users);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
      }
    };
    
    loadUsersData();
  }, []);

  useEffect(() => {
    if (request) {
      setFormData({
        tipo_campana: request.tipo_campana || tipoSolicitud,
        plataforma: request.plataforma || 'google',
        nombre_cliente: request.nombre_cliente || '',
        nombre_campana: request.nombre_campana || '',
        ubicacion_objetivo: request.ubicacion_objetivo || '',
        radio_km: request.radio_km || '',
        palabras_clave: request.palabras_clave || '',
        presupuesto_diario: request.presupuesto_diario || '',
        landing_page: request.landing_page || '',
        link_cuenta_ads: request.link_cuenta_ads || '',
        asignado_a: request.asignado_a || '',
        observaciones: request.observaciones || '',
        prioridad: request.prioridad || 'normal',
        estado: request.estado || 'pendiente'
      });
    } else {
      // Si es nueva solicitud, asignar al usuario actual por defecto
      setFormData(prev => ({
        ...prev,
        tipo_campana: tipoSolicitud,
        plataforma: 'google',
        asignado_a: currentUser?.email || ''
      }));
    }
  }, [request, currentUser, tipoSolicitud]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre_cliente || !formData.nombre_campana) {
      showNotification('Por favor completa todos los campos requeridos', 'error');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        created_by: request ? undefined : (currentUser?.name || currentUser?.email || 'Sistema'),
        updated_by: request ? (currentUser?.name || currentUser?.email || 'Sistema') : undefined
      };

      if (request) {
        await updateAdsRequest(request.id, dataToSend);
        showNotification('Solicitud actualizada exitosamente', 'success');
      } else {
        await createAdsRequest(dataToSend);
        showNotification('Solicitud creada exitosamente', 'success');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      showNotification(error.message || 'Error al guardar la solicitud', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {request ? 'Editar Solicitud' : `Nueva Solicitud - Campaña ${formData.tipo_campana === 'nueva' ? 'Nueva' : 'Actual'}`}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Plataforma y Tipo de Campaña */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plataforma *
              </label>
              <select
                name="plataforma"
                value={formData.plataforma}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="google">Google Ads</option>
                <option value="meta">Meta Ads (Facebook/Instagram)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Campaña *
              </label>
              <select
                name="tipo_campana"
                value={formData.tipo_campana}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                disabled={!request}
                required
              >
                <option value="nueva">Campaña Nueva</option>
                <option value="actual">Campaña Actual</option>
              </select>
              {!request && (
                <p className="mt-1 text-xs text-gray-500">
                  El tipo de campaña se seleccionó al hacer clic en el botón correspondiente
                </p>
              )}
            </div>
          </div>

          {/* Nombre del Cliente y Nombre de Campaña */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del Cliente *
              </label>
              <input
                type="text"
                name="nombre_cliente"
                value={formData.nombre_cliente}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: Empresa ABC"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de la Campaña *
              </label>
              <input
                type="text"
                name="nombre_campana"
                value={formData.nombre_campana}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: Campaña Verano 2024"
                required
              />
            </div>
          </div>

          {/* Ubicación Objetivo y Radio */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="inline mr-1" size={16} />
                Ubicación Objetivo
              </label>
              <input
                type="text"
                name="ubicacion_objetivo"
                value={formData.ubicacion_objetivo}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: España, Madrid, Latinoamérica"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Radio (km)
              </label>
              <input
                type="number"
                name="radio_km"
                value={formData.radio_km}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: 50"
              />
            </div>
          </div>

          {/* Palabras Clave */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Key className="inline mr-1" size={16} />
              Palabras Clave
            </label>
            <textarea
              name="palabras_clave"
              value={formData.palabras_clave}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Ej: marketing digital, publicidad online, SEO, SEM..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Separa las palabras clave con comas
            </p>
          </div>

          {/* Presupuesto Diario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSign className="inline mr-1" size={16} />
              Presupuesto Diario
            </label>
            <input
              type="number"
              name="presupuesto_diario"
              value={formData.presupuesto_diario}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>

          {/* Landing Page y Link Cuenta Ads */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <LinkIcon className="inline mr-1" size={16} />
                Landing Page
              </label>
              <input
                type="url"
                name="landing_page"
                value={formData.landing_page}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://ejemplo.com/landing"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <LinkIcon className="inline mr-1" size={16} />
                Link Cuenta Google Ads
              </label>
              <input
                type="url"
                name="link_cuenta_ads"
                value={formData.link_cuenta_ads}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://ads.google.com/..."
              />
            </div>
          </div>

          {/* Observaciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FileText className="inline mr-1" size={16} />
              Observaciones
            </label>
            <textarea
              name="observaciones"
              value={formData.observaciones}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Detalles adicionales sobre la solicitud..."
            />
          </div>

          {/* Prioridad y Agente Asignado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <AlertCircle className="inline mr-1" size={16} />
                Prioridad
              </label>
              <select
                name="prioridad"
                value={formData.prioridad}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="baja">Baja</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="inline mr-1" size={16} />
                Agente Asignado
              </label>
              <select
                name="asignado_a"
                value={formData.asignado_a}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleccionar agente...</option>
                {availableUsers.map(user => {
                  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                  return (
                    <option key={user.id} value={user.email}>
                      {fullName || user.email}
                      {user.role && ` (${user.role})`}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              name="estado"
              value={formData.estado}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="pendiente">Pendiente</option>
              <option value="en_proceso">En Proceso</option>
              <option value="aprobado">Aprobado</option>
              <option value="rechazado">Rechazado</option>
              <option value="completado">Completado</option>
            </select>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (request ? 'Actualizar' : 'Crear Solicitud')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdsRequestForm;
